import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from '../Librarian/librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../Constant/useActiveLibrary';
import Loader from '../Layout/Loader';
import useMinLoading from '../Layout/useMinLoading';
import { getOverdueBooksReport, getErrorMessage } from '../api/api';
import '../Librarian/lib-page-header.css';
import '../Librarian/BookList.css';

/**
 * Overdue Books — a School-scoped view with a quick "Return" action per row,
 * kept separate from the centralized Reports module's Overdue Books Report
 * for that action button. It reuses that exact same report endpoint/query
 * (server-computed overdue_days via GREATEST(DATEDIFF(...),0), same fields)
 * instead of independently re-deriving overdue status/day-counts from raw
 * issues client-side, so there is only ever one overdue formula in the app.
 */
const OverdueBooks = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [issues, setIssues] = useState([]);
  const [search, setSearch] = useState('');

  const load = async () => {
    if (!libraryId) return;
    try {
      setLoading(true);
      // page_size capped consistent with the backend's report page-size
      // safety bound (see models paginate()) — this screen shows the same
      // "every currently overdue issue" data as the Reports module report.
      const res = await getOverdueBooksReport(libraryId, { page_size: 500 });
      setIssues(res && res.success !== false && Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const overdue = useMemo(() => {
    const q = search.trim().toLowerCase();
    return issues
      .map((i) => ({
        ...i,
        __member: i.member_name || `Member #${i.member_id ?? ''}`,
        __title: i.book_title || 'Book',
        __days: i.overdue_days
      }))
      .filter((i) => {
        if (!q) return true;
        return (`${i.__member} ${i.__title}`).toLowerCase().includes(q);
      })
      .sort((a, b) => b.__days - a.__days);
  }, [issues, search]);

  const fmt = (d) => (d ? String(d).slice(0, 10) : '—');

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-exclamation-triangle" /></div>
          <div>
            <h3 className="lib-page-header__title">Overdue Books</h3>
            <div className="lib-page-header__subtitle">Issued books past their due date</div>
          </div>
        </div>
      </div>

      <div className="book-filter-card">
        <div className="book-filter-row">
          <div className="search__input" style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              className="book-search-input"
              placeholder="Search by member or book..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <span style={{ color: '#6b7a99', fontSize: 13, fontWeight: 600 }}>{overdue.length} overdue</span>
        </div>
      </div>

      {showLoader ? (
        <Loader />
      ) : (
        <div className="book-table-card">
          <div className="book-table-wrapper">
            <table className="book-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Membership No.</th>
                  <th>Book</th>
                  <th>Accession No.</th>
                  <th>Issued On</th>
                  <th>Due Date</th>
                  <th>Days Overdue</th>
                  <th>Fine</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {overdue.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="book-empty">
                      <i className="fas fa-check-circle" style={{ color: '#16a34a', marginRight: 8 }} />
                      No overdue books. All caught up!
                    </td>
                  </tr>
                ) : (
                  overdue.map((i) => (
                    <tr key={i.id}>
                      <td><strong>{i.__member}</strong></td>
                      <td>{i.membership_no || '—'}</td>
                      <td>{i.__title}</td>
                      <td>{i.accession_no || '—'}</td>
                      <td>{fmt(i.issue_date)}</td>
                      <td>{fmt(i.due_date)}</td>
                      <td><span className="book-count book-count-yellow">{i.__days} day{i.__days === 1 ? '' : 's'}</span></td>
                      <td>{i.fine_amount != null ? `₹${Number(i.fine_amount).toLocaleString('en-IN')}` : '—'}</td>
                      <td>
                        <div className="book-action-buttons">
                          <button className="book-action-btn status" onClick={() => navigate(`${sectionBase}/issues/${i.id}/return`)}>
                            Return
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverdueBooks;
